import { Head, Link, router } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { useState } from "react"

import { AppShell } from "@/components/AppShell"
import { TabStrip } from "@/components/bids/TabStrip"
import { RollupCard } from "@/components/order-templates/RollupCard"
import { TemplateLineDialog } from "@/components/order-templates/TemplateLineDialog"
import { TemplateLinesTable } from "@/components/order-templates/TemplateLinesTable"
import { VersionRail } from "@/components/order-templates/VersionRail"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { OrderTemplatesProps, TemplateLine } from "@/types/bids"

const DESCRIPTION =
  "Order templates: everything a job needs beyond product and labor, one versioned template per job type."

export default function OrderTemplatesIndex({
  job_types,
  active,
  template,
  back,
}: OrderTemplatesProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingLine, setEditingLine] = useState<TemplateLine | null>(null)

  const switchTab = (key: string) =>
    router.get(
      "/order_templates",
      // Keep the workspace context across tab switches.
      back ? { job_type: key, community: back.community_id } : { job_type: key },
      { preserveState: true, preserveScroll: true }
    )

  const openCreate = () => {
    setEditingLine(null)
    setDialogOpen(true)
  }

  const openEdit = (line: TemplateLine) => {
    setEditingLine(line)
    setDialogOpen(true)
  }

  return (
    <>
      <Head title="Order templates">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Order templates" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell wide>
        <p className="text-sm text-ink-muted">
          <Link href="/bids" className="hover:text-ink-body">
            Bids
          </Link>{" "}
          {back && (
            <>
              /{" "}
              <Link href={back.url} className="hover:text-ink-body">
                {back.label}
              </Link>{" "}
            </>
          )}
          / <strong>Order templates</strong>
        </p>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4 border-b border-hairline pb-6">
          <div>
            <h1>Order templates</h1>
            <p className="mt-1 max-w-3xl">
              Everything a job needs beyond product + labor — one template per job type, applied
              across all levels of the category. The template does two jobs: its $/SF roll-up
              feeds every level&apos;s COGS in the bid workspace, and its lines become the ERP
              order later.
            </p>
          </div>
          {back && (
            <Button asChild variant="secondary" data-testid="back-to-workspace">
              <Link href={back.url}>← Back to {back.label}&apos;s bid</Link>
            </Button>
          )}
        </div>

        <TabStrip className="mt-6">
          {job_types.map((tab) => (
            <button
              key={tab.key}
              onClick={() => switchTab(tab.key)}
              className={`whitespace-nowrap border-b-2 pb-2.5 font-medium ${
                active === tab.key
                  ? "border-accent text-ink-display"
                  : "border-transparent text-ink-muted hover:text-ink-body"
              }`}
              data-testid={`template-tab-${tab.key}`}
            >
              {tab.label}
              <span className="ml-1.5 font-normal text-ink-muted">
                {tab.current_version ? `v${tab.current_version}` : "—"}
                {tab.line_count > 0 && ` · ${tab.line_count}`}
              </span>
              {tab.dirty && (
                <Badge tone="muted" className="ml-1.5">
                  draft
                </Badge>
              )}
            </button>
          ))}
        </TabStrip>

        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="space-y-4">
            <TemplateLinesTable lines={template.draft.lines} onEdit={openEdit} />
            <Button variant="soft" onClick={openCreate} data-testid="add-template-line">
              <Plus className="mr-1.5 h-4 w-4" /> Add line
            </Button>
            <RollupCard template={template} />
          </div>
          <VersionRail template={template} />
        </div>

        <TemplateLineDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          addUrl={template.urls.add_line}
          line={editingLine}
        />
      </AppShell>
    </>
  )
}
